// Form de criar atividade/tarefa CRM (Story 11.8).
// - Alvo (lead/deal/client) vem do componente pai — não é escolhido no form.
// - Tipos system-only (mudanca_estagio, proposta_enviada) NÃO aparecem (AC2).
// - type=tarefa revela o campo de data de vencimento (obrigatório — AC2).
// - Submit desabilitado enquanto pending (AC10). Toast de sucesso/erro.

import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import type { CrmActivityTypeSchema } from '@control-center/shared';

import { useCreateCrmActivity } from '../../hooks/use-crm-activities';
import { extractErrorMessage } from '../../lib/errors';
import type { ActivityTimelineTarget } from './ActivityTimeline';
import { Field, Input, Select, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';

interface ActivityFormProps {
  target: ActivityTimelineTarget;
  /** Callback após criar com sucesso (ex: fechar painel). */
  onCreated?: () => void;
}

/** Tipos criáveis manualmente (system-only ficam de fora — AC2). */
const TYPE_OPTIONS: { value: CrmActivityTypeSchema; label: string }[] = [
  { value: 'ligacao', label: 'Ligação' },
  { value: 'mensagem', label: 'Mensagem' },
  { value: 'reuniao', label: 'Reunião' },
  { value: 'email', label: 'E-mail' },
  { value: 'nota', label: 'Nota' },
  { value: 'tarefa', label: 'Tarefa (com data)' },
];

function targetPayload(
  target: ActivityTimelineTarget,
): { lead_id: string } | { deal_id: string } | { client_id: string } {
  switch (target.kind) {
    case 'lead':
      return { lead_id: target.id };
    case 'deal':
      return { deal_id: target.id };
    case 'client':
      return { client_id: target.id };
  }
}

export function ActivityForm({ target, onCreated }: ActivityFormProps): JSX.Element {
  const create = useCreateCrmActivity();

  const [type, setType] = useState<CrmActivityTypeSchema>('nota');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');

  const isTask = type === 'tarefa';

  const canSubmit = useMemo(() => {
    if (subject.trim().length === 0) return false;
    if (isTask && dueDate.trim().length === 0) return false;
    return !create.isPending;
  }, [subject, isTask, dueDate, create.isPending]);

  const reset = (): void => {
    setType('nota');
    setSubject('');
    setDescription('');
    setDueDate('');
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;

    create.mutate(
      {
        type,
        subject: subject.trim(),
        description: description.trim() ? description.trim() : null,
        due_date: isTask ? dueDate : null,
        occurred_at: null,
        ...targetPayload(target),
      } as never,
      {
        onSuccess: () => {
          toast.success(isTask ? 'Tarefa criada.' : 'Atividade registrada.');
          reset();
          onCreated?.();
        },
        onError: (err) => {
          toast.error('Não conseguimos registrar.', {
            description: extractErrorMessage(err, 'Tente de novo em instantes.'),
          });
        },
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 rounded-[14px] border border-line bg-panel p-4"
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[160px_1fr]">
        <Field label="Tipo">
          {({ inputId }) => (
            <Select
              id={inputId}
              value={type}
              onChange={(e) => setType(e.target.value as CrmActivityTypeSchema)}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Assunto" required>
          {({ inputId }) => (
            <Input
              id={inputId}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={isTask ? 'O que precisa ser feito' : 'Resumo da atividade'}
              maxLength={200}
            />
          )}
        </Field>
      </div>

      {isTask && (
        <Field label="Vence em" required hint="Data da tarefa (obrigatória pra tarefas)">
          {({ inputId }) => (
            <Input
              id={inputId}
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          )}
        </Field>
      )}

      <Field label="Detalhes">
        {({ inputId }) => (
          <Textarea
            id={inputId}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Opcional"
            maxLength={5000}
          />
        )}
      </Field>

      <div className="flex justify-end">
        <PillButton solid type="submit" disabled={!canSubmit}>
          {create.isPending ? 'Salvando…' : isTask ? 'Criar tarefa' : 'Registrar'}
        </PillButton>
      </div>
    </form>
  );
}
